/**
 * Lending Configuration -- the institution's own lending parameters (GH #4688).
 *
 * Route:  /admin/config/lending
 * Scope:  FI-tenant (admin.aaim.com : 9300, adminPortal), in the FI
 *         Configuration nav group, gated on settings:update for fi_admin, and
 *         as the one read-only item of the loan officer's ungated FI
 *         Configuration group (Rob's ruling 2026-10-29 on GH #4688). The
 *         2026-08-20 ruling of the lending-platform canon-alignment arc places
 *         lending configuration here, for the institution's administrators;
 *         Aaim operators reach it by tenant impersonation.
 *
 * Reads and saves the caller's own institution's configuration through
 * lending-platform's GET and PUT /api/v1/lending-config, and offers
 * /api/v1/lending-config/defaults as a starting point. No request names a
 * tenant: lending-platform resolves the session tenant.
 *
 * Who may save is the spec's rule (lendingConfigUpdate): role fi_admin or a
 * platform-administrator role, compared case-insensitively. Every other role
 * sees the values and no form; lending-platform refuses that role's PUT with
 * 403 regardless.
 *
 * A session that acts for the apex tenant, a platform administrator who is
 * not impersonating an institution, gets a notice and no form or values:
 * lending-platform resolves that session to apex, so a save would write apex's
 * configuration under this page's "your institution" copy. The response
 * carries no tenant id, so the save cannot name the tenant the page read
 * (master review on PR #4692, 2026-10-30, item 1).
 *
 * Nothing is filled from the defaults (GH #2459). An institution with no saved
 * configuration (404 LENDING_CONFIGURATION_MISSING) or a saved one lacking a
 * field (503 LENDING_CONFIGURATION_INCOMPLETE) is shown as such; an editor
 * loads the platform defaults into the form, reviews them and saves a complete
 * configuration, sending every field. Over a saved configuration a save sends
 * only the fields the editor changed, haircuts per asset class, since the PUT
 * merges them over the stored values; an untouched value is never re-sent
 * (master review item 2).
 *
 * Units, as the spec states them: interestRate is a percent (6.5 = 6.5%);
 * ltvWarningThreshold, ltvCriticalThreshold and each haircut are fractions
 * (0.80 = 80%), edited here as a percent and converted by moving the decimal
 * point, never rounded (0.12345 shows as 12.345); maxLoanAmount is dollars;
 * defaultTermDays is a whole number of days. baseRate and floorRate are edited
 * as a percent, the reading platform-admin's page gives them. The two
 * thresholds are risk scores.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Alert, BodyText, Button, Card, Heading, Input } from '@aaimplatform/ui';
import { Loader2 } from 'lucide-react';
import {
  isLendingConfigurationIncomplete,
  isLendingConfigurationMissing,
  LendingConfigRequestError,
  type LendingConfiguration,
  type LendingConfigurationUpdateRequest,
} from '../../api/services/lending-config';
import {
  useLendingConfig,
  useLendingConfigDefaults,
  useUpdateLendingConfig,
} from '../../hooks/useLendingConfig';
import { useAuthStore } from '../../stores/auth';
import { hasPlatformAdminRole } from '../../lib/platform-admin-roles';
import { APEX_TENANT_ID, resolveOrgContext, type OrgContextUser } from '../../layouts/org-context';
import { formatMoney, formatPercent } from '../../utils/format';

/** The role lendingConfigUpdate admits besides the platform-administrator roles. */
const FI_ADMIN_ROLE = 'fi_admin';

/** True when the roles may save: fi_admin or a platform-admin role, lowercased. */
export function canEditLendingConfiguration(roles: readonly unknown[] | null | undefined): boolean {
  if (hasPlatformAdminRole(roles)) return true;
  return (roles ?? []).some(
    (role) => typeof role === 'string' && role.toLowerCase() === FI_ADMIN_ROLE,
  );
}

/**
 * True when the session acts for the apex tenant (Aaim, Inc.): a platform
 * administrator who is not impersonating an institution, or a session with no
 * tenant, by the server resolver's rules as layouts/org-context.ts mirrors
 * them. lending-platform resolves such a session to apex.
 */
export function actsForApexTenant(user: OrgContextUser | null | undefined): boolean {
  return resolveOrgContext(user, undefined).currentOrg.id === APEX_TENANT_ID;
}

type ScalarField = Exclude<keyof LendingConfiguration, 'haircuts'>;

/** The form's sections: each field's label, its unit and its helper text. */
const SECTIONS: Array<{
  title: string;
  fields: Array<{
    field: ScalarField;
    label: string;
    unit: 'percent' | 'ratio' | 'dollars' | 'days' | 'score';
    helperText: string;
  }>;
}> = [
  {
    title: 'Interest rates',
    fields: [
      { field: 'interestRate', label: 'Interest rate (%)', unit: 'percent', helperText: 'Annual interest rate applied to loans.' },
      { field: 'baseRate', label: 'Base rate (%)', unit: 'percent', helperText: 'Base interest rate used for playbook calculations.' },
      { field: 'floorRate', label: 'Floor rate (%)', unit: 'percent', helperText: 'Minimum interest rate floor.' },
    ],
  },
  {
    title: 'LTV thresholds',
    fields: [
      { field: 'ltvWarningThreshold', label: 'Warning threshold (% LTV)', unit: 'ratio', helperText: 'The loan-to-value ratio at which a warning alert is raised.' },
      { field: 'ltvCriticalThreshold', label: 'Critical threshold (% LTV)', unit: 'ratio', helperText: 'The loan-to-value ratio at which a critical alert is raised.' },
    ],
  },
  {
    title: 'Loan parameters',
    fields: [
      { field: 'maxLoanAmount', label: 'Maximum loan amount (USD)', unit: 'dollars', helperText: 'The largest loan amount, in dollars.' },
      { field: 'defaultTermDays', label: 'Default term (days)', unit: 'days', helperText: 'The default loan term, a whole number of days.' },
    ],
  },
  {
    title: 'Credit decision automation',
    fields: [
      { field: 'autoApproveThreshold', label: 'Auto-approve threshold (risk score)', unit: 'score', helperText: 'The minimum risk score for automatic approval.' },
      { field: 'autoDenyThreshold', label: 'Auto-deny threshold (risk score)', unit: 'score', helperText: 'The maximum risk score for automatic denial.' },
    ],
  },
];

const SCALAR_FIELDS = SECTIONS.flatMap((section) => section.fields);

type FormState = { scalars: Record<ScalarField, string>; haircuts: Record<string, string> };
type FormErrorState = { scalars: Partial<Record<ScalarField, string>>; haircuts: Record<string, string> };

/**
 * The decimal string times 10^places, by moving the decimal point in the
 * string's exponent. Float multiplication would turn 57.7 / 100 into
 * 0.5770000000000001; this reads "57.7e-2" as 0.577.
 */
function shiftDecimal(value: string, places: number): number | null {
  const [mantissa, exponent = '0', extra] = value.trim().toLowerCase().split('e');
  if (extra !== undefined || mantissa === '') return null;
  const exp = Number(exponent);
  if (!Number.isInteger(exp)) return null;
  const shifted = Number(`${mantissa}e${exp + places}`);
  return Number.isFinite(shifted) ? shifted : null;
}

/** A fraction (0.8) as the editable percent "80", unrounded (0.12345 -> "12.345"). */
export function ratioToPercentInput(ratio: number): string {
  if (!Number.isFinite(ratio)) return '';
  const percent = shiftDecimal(String(ratio), 2);
  return percent === null ? '' : String(percent);
}

/** The percent "80" back to the fraction 0.8, unrounded, or null when not a number. */
export function percentInputToRatio(value: string): number | null {
  if (parseNumber(value) === null) return null;
  return shiftDecimal(value, -2);
}

function parseNumber(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

/** The haircut key in words: EQUITY -> "Equity", FIXED_INCOME -> "Fixed income". */
export function haircutLabel(key: string): string {
  const words = key.toLowerCase().split('_').filter(Boolean).join(' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function formFrom(config: LendingConfiguration): FormState {
  const scalars = {} as Record<ScalarField, string>;
  for (const { field, unit } of SCALAR_FIELDS) {
    scalars[field] = unit === 'ratio' ? ratioToPercentInput(config[field]) : String(config[field]);
  }
  const haircuts: Record<string, string> = {};
  for (const [key, ratio] of Object.entries(config.haircuts ?? {})) {
    haircuts[key] = ratioToPercentInput(ratio);
  }
  return { scalars, haircuts };
}

function sameForm(a: FormState, b: FormState): boolean {
  const keys = (record: Record<string, string>) => Object.keys(record).sort();
  return (
    SCALAR_FIELDS.every(({ field }) => a.scalars[field] === b.scalars[field]) &&
    JSON.stringify(keys(a.haircuts)) === JSON.stringify(keys(b.haircuts)) &&
    Object.keys(a.haircuts).every((key) => a.haircuts[key] === b.haircuts[key])
  );
}

/**
 * The request body when every field is a number (defaultTermDays a whole
 * number), else the message per field. The spec states no other bound, and
 * lending-platform stays the authority on a save.
 */
export function validateLendingForm(
  form: FormState,
): { ok: true; body: LendingConfiguration } | { ok: false; errors: FormErrorState } {
  const errors: FormErrorState = { scalars: {}, haircuts: {} };
  const body = { haircuts: {} } as LendingConfiguration;

  for (const { field, unit } of SCALAR_FIELDS) {
    const raw = form.scalars[field];
    if (unit === 'ratio') {
      const ratio = percentInputToRatio(raw);
      if (ratio === null) errors.scalars[field] = 'Enter a percent, for example 80.';
      else body[field] = ratio;
    } else if (unit === 'days') {
      const days = parseNumber(raw);
      if (days === null || !Number.isInteger(days)) errors.scalars[field] = 'Enter a whole number of days.';
      else body[field] = days;
    } else {
      const value = parseNumber(raw);
      if (value === null) errors.scalars[field] = 'Enter a number.';
      else body[field] = value;
    }
  }
  for (const [key, raw] of Object.entries(form.haircuts)) {
    const ratio = percentInputToRatio(raw);
    if (ratio === null) errors.haircuts[key] = 'Enter a percent, for example 25.';
    else body.haircuts[key] = ratio;
  }

  const failed = Object.keys(errors.scalars).length > 0 || Object.keys(errors.haircuts).length > 0;
  return failed ? { ok: false, errors } : { ok: true, body };
}

/**
 * The PUT body for a save over a saved configuration: only the fields whose
 * form value differs from the one the stored configuration seeded, haircuts
 * per asset class. lendingConfigUpdate merges them over the stored values.
 */
export function changedLendingFields(
  body: LendingConfiguration,
  form: FormState,
  baseline: FormState,
): LendingConfigurationUpdateRequest {
  const changed: LendingConfigurationUpdateRequest = {};
  for (const { field } of SCALAR_FIELDS) {
    if (form.scalars[field] !== baseline.scalars[field]) changed[field] = body[field];
  }
  const haircuts: Record<string, number> = {};
  for (const key of Object.keys(form.haircuts)) {
    if (form.haircuts[key] !== baseline.haircuts[key]) haircuts[key] = body.haircuts[key];
  }
  if (Object.keys(haircuts).length > 0) changed.haircuts = haircuts;
  return changed;
}

function displayValue(
  config: LendingConfiguration,
  { field, unit }: (typeof SCALAR_FIELDS)[number],
): string {
  const value = config[field];
  switch (unit) {
    case 'ratio':
      return formatPercent(value, 1);
    case 'percent':
      return `${value}%`;
    case 'dollars':
      return formatMoney(value);
    case 'days':
      return `${value} days`;
    default:
      return String(value);
  }
}

function ReadOnlyValues({ config }: { config: LendingConfiguration }) {
  const rows: Array<[string, string]> = [
    ...SCALAR_FIELDS.map((spec): [string, string] => [spec.label, displayValue(config, spec)]),
    ...Object.entries(config.haircuts ?? {}).map(([key, ratio]): [string, string] => [
      `${haircutLabel(key)} haircut`,
      formatPercent(ratio, 1),
    ]),
  ];
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm font-sans">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-secondary">{label}</dt>
          <dd>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

const READER_NOTE =
  'Your role can view this configuration. Saving it needs the FI administrator role, which your institution’s administrator holds.';

const APEX_NOTE =
  'This session acts for Aaim, Inc., not for an institution, so this page shows and saves no lending configuration. Choose an institution in the organization switcher to view and change its configuration.';

export function LendingConfigurationPage() {
  const { user } = useAuthStore();
  const forApex = actsForApexTenant(user);
  const canEdit = !forApex && canEditLendingConfiguration(user?.roles);

  const configQuery = useLendingConfig();
  const defaultsQuery = useLendingConfigDefaults();
  const update = useUpdateLendingConfig();

  const [form, setForm] = useState<FormState | null>(null);
  const [fromDefaults, setFromDefaults] = useState(false);
  const [errors, setErrors] = useState<FormErrorState>({ scalars: {}, haircuts: {} });
  const [saved, setSaved] = useState(false);

  // Seed the form from the stored configuration, and again after a save
  // writes the stored values into the query cache.
  useEffect(() => {
    if (configQuery.data) {
      setForm(formFrom(configQuery.data));
      setFromDefaults(false);
    }
  }, [configQuery.data]);

  const missing = isLendingConfigurationMissing(configQuery.error);
  const incomplete = isLendingConfigurationIncomplete(configQuery.error);
  const missingFields =
    incomplete && configQuery.error instanceof LendingConfigRequestError
      ? configQuery.error.missingFields
      : [];

  const baseline = configQuery.data ? formFrom(configQuery.data) : null;
  const dirty = form !== null && (fromDefaults || (baseline !== null && !sameForm(form, baseline)));

  const clearMessages = () => {
    setSaved(false);
    update.reset();
  };

  const setScalar = (field: ScalarField) => (event: { target: { value: string } }) => {
    const value = event.target.value;
    setForm((current) => (current ? { ...current, scalars: { ...current.scalars, [field]: value } } : current));
    setErrors((current) => ({ ...current, scalars: { ...current.scalars, [field]: undefined } }));
    setSaved(false);
  };

  const setHaircut = (key: string) => (event: { target: { value: string } }) => {
    const value = event.target.value;
    setForm((current) => (current ? { ...current, haircuts: { ...current.haircuts, [key]: value } } : current));
    setErrors((current) => {
      const haircuts = { ...current.haircuts };
      delete haircuts[key];
      return { ...current, haircuts };
    });
    setSaved(false);
  };

  const loadDefaults = async () => {
    clearMessages();
    const result = await defaultsQuery.refetch();
    if (result.data) {
      setForm(formFrom(result.data));
      setFromDefaults(true);
      setErrors({ scalars: {}, haircuts: {} });
    }
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form) return;
    clearMessages();
    const result = validateLendingForm(form);
    if (!result.ok) {
      setErrors(result.errors);
      return;
    }
    setErrors({ scalars: {}, haircuts: {} });
    const body = fromDefaults || !baseline ? result.body : changedLendingFields(result.body, form, baseline);
    update.mutate(body, { onSuccess: () => setSaved(true) });
  };

  const handleReset = () => {
    clearMessages();
    setErrors({ scalars: {}, haircuts: {} });
    if (baseline) {
      setForm(baseline);
      setFromDefaults(false);
    } else {
      setForm(null);
      setFromDefaults(false);
    }
  };

  const unsavedState = missing || incomplete;

  return (
    <div>
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); the body opens
            with the descriptive subtitle, no redundant heading. */}
        <p className="text-sm font-sans text-tertiary leading-normal max-w-3xl">
          Your institution&apos;s lending parameters: interest rates, the LTV thresholds that raise
          collateral alerts, loan limits, the risk-score thresholds for automatic credit decisions,
          and the haircut applied to each asset class. A save applies to your institution only.
        </p>
      </div>

      {forApex ? (
        <Alert type="warning" title="Choose an institution">
          <BodyText variant="ui" size="sm">
            {APEX_NOTE}
          </BodyText>
        </Alert>
      ) : configQuery.isLoading ? (
        <div className="flex items-center gap-2 text-secondary">
          <Loader2 className="w-4 h-4 animate-spin" />
          <BodyText variant="ui" size="sm">
            Loading your lending configuration...
          </BodyText>
        </div>
      ) : configQuery.error && !unsavedState ? (
        <Alert type="error" title="Could not load the lending configuration">
          <BodyText variant="ui" size="sm">
            {configQuery.error.message}
          </BodyText>
        </Alert>
      ) : (
        <Card className="p-6 max-w-3xl">
          <div className="space-y-4">
            {/* Under AppHeader's h1, so an h2 styled as a card title. */}
            <Heading level={2} as={3} size="card">
              Lending configuration
            </Heading>

            {missing && (
              <Alert type="warning" title="No lending configuration saved">
                <BodyText variant="ui" size="sm">
                  Your institution has not saved a lending configuration, and none is filled in
                  from the platform defaults.
                  {canEdit
                    ? ' Load the platform defaults, review each value and save to set one.'
                    : ' Your institution’s administrator can set one.'}
                </BodyText>
              </Alert>
            )}
            {incomplete && (
              <Alert type="warning" title="The saved lending configuration is incomplete">
                <BodyText variant="ui" size="sm">
                  {missingFields.length > 0
                    ? `It lacks ${missingFields.join(', ')}.`
                    : 'It lacks one or more fields.'}
                  {canEdit
                    ? ' Load the platform defaults, review each value and save a complete configuration.'
                    : ' Your institution’s administrator can complete it.'}
                </BodyText>
              </Alert>
            )}

            {!canEdit ? (
              <>
                {configQuery.data && <ReadOnlyValues config={configQuery.data} />}
                <BodyText variant="ui" size="sm" color="secondary">
                  {READER_NOTE}
                </BodyText>
              </>
            ) : (
              <>
                {defaultsQuery.error && (
                  <Alert type="error" role="alert" title="Could not load the platform defaults">
                    <BodyText variant="ui" size="sm">
                      {defaultsQuery.error.message}
                    </BodyText>
                  </Alert>
                )}
                {fromDefaults && (
                  <BodyText variant="ui" size="sm" color="secondary">
                    The form holds the platform defaults. Nothing is stored until you save.
                  </BodyText>
                )}
                {unsavedState && !form && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="small"
                    disabled={defaultsQuery.isFetching}
                    onClick={() => void loadDefaults()}
                  >
                    {defaultsQuery.isFetching ? 'Loading defaults...' : 'Load platform defaults'}
                  </Button>
                )}
                {form && (
                  <form onSubmit={handleSubmit} noValidate className="space-y-6">
                    {update.error && (
                      <Alert type="error" role="alert" title="Aaim refused this change">
                        <BodyText variant="ui" size="sm">
                          {update.error.message}
                        </BodyText>
                      </Alert>
                    )}
                    {/* A persistent live region, so the success notice is
                        announced when it appears (Alert's role guidance). */}
                    <div role="status">
                      {saved && !dirty && (
                        <Alert type="success">
                          <BodyText variant="ui" size="sm">
                            Saved. This is your institution&apos;s lending configuration.
                          </BodyText>
                        </Alert>
                      )}
                    </div>

                    {SECTIONS.map((section) => (
                      <fieldset key={section.title} className="space-y-4">
                        <legend className="text-sm font-sans font-semibold mb-2">{section.title}</legend>
                        {section.fields.map(({ field, label, unit, helperText }) => (
                          <Input
                            key={field}
                            id={`lending-${field}`}
                            name={field}
                            label={label}
                            type="number"
                            inputMode={unit === 'days' ? 'numeric' : 'decimal'}
                            step={unit === 'days' ? 1 : 'any'}
                            value={form.scalars[field]}
                            onChange={setScalar(field)}
                            error={errors.scalars[field]}
                            helperText={helperText}
                            required
                          />
                        ))}
                      </fieldset>
                    ))}

                    {Object.keys(form.haircuts).length > 0 && (
                      <fieldset className="space-y-4">
                        <legend className="text-sm font-sans font-semibold mb-2">Asset haircuts</legend>
                        {Object.keys(form.haircuts)
                          .sort()
                          .map((key) => (
                            <Input
                              key={key}
                              id={`lending-haircut-${key}`}
                              name={`haircuts.${key}`}
                              label={`${haircutLabel(key)} haircut (%)`}
                              type="number"
                              inputMode="decimal"
                              step="any"
                              value={form.haircuts[key]}
                              onChange={setHaircut(key)}
                              error={errors.haircuts[key]}
                              required
                            />
                          ))}
                      </fieldset>
                    )}

                    <div className="flex items-center gap-3 pt-2">
                      <Button
                        type="submit"
                        variant="primary"
                        size="small"
                        disabled={!dirty || update.isPending}
                      >
                        {update.isPending ? 'Saving...' : 'Save configuration'}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="small"
                        disabled={!dirty || update.isPending}
                        onClick={handleReset}
                      >
                        Reset
                      </Button>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
